/** IndexedDB. CryptoKey objects are stored directly: a non-extractable key
 * survives structured cloning without ever exposing its bytes. */
import { FRESHNESS_WINDOW_S } from "./constants";
import type { StoredStatusList } from "./status";

const DB_NAME = "uh-autentica";
const STORE = "kv";

/**
 * How long a nonce is remembered. Derived, not spelled as 121: written as a
 * literal, a later change to `FRESHNESS_WINDOW_S` would move one half of the
 * argument and not the other, and nothing would say so.
 *
 * `verify.ts` names both halves of the reason: a caller that never evicts
 * grows the seen-set without bound, and one that evicts too soon reopens the
 * replay. The `+ 1` is which side "too soon" falls on. The freshness window is
 * two-sided, so one presentation is accepted at every verifier clock from
 * `timestamp - 60` to `timestamp + 60` inclusive, and the two ends of that are
 * 120 seconds apart. At exactly two windows the boundary was reachable: first
 * seen at `t - 60`, replayed at `t + 60`, `now - at` is 120, the `<` below
 * evicts it, and the presentation is still just fresh enough to verify. One
 * second more than the span a presentation can live through closes it.
 */
const NONCE_MEMORY_S = FRESHNESS_WINDOW_S * 2 + 1;

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * `close` in a `finally`, in all three of these, because the rejecting path is
 * the one that repeats. `screens/scan.ts` catches exactly these rejections and
 * keeps scanning, so a `close` after the await leaked one connection per scan
 * on a phone already failing at its storage quota.
 */
async function put(key: string, value: unknown): Promise<void> {
  const db = await open();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/**
 * Read-modify-write inside one `readwrite` transaction.
 *
 * `get` and `put` in separate transactions leave a window wide enough to lose
 * a write: two calls in flight at once both read the same value, and the
 * second one writes back a change computed from a value the first had already
 * superseded. For the nonce map that is a dropped nonce, and a dropped nonce
 * is a replay this app accepts.
 *
 * IndexedDB already has the primitive, so there is no mutex here. Overlapping
 * `readwrite` transactions whose scopes overlap are serialised by the browser,
 * and issuing the `put` from the `get`'s success handler holds this one open
 * across the change rather than letting it auto-commit after the read.
 */
async function update<T>(key: string, change: (current: T | null) => T): Promise<void> {
  const db = await open();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const request = store.get(key);
      request.onsuccess = () => store.put(change((request.result as T) ?? null), key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      // A throw inside the success handler aborts the transaction without
      // firing `error`. Without this the promise would never settle and the
      // caller would hang instead of failing.
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function get<T>(key: string): Promise<T | null> {
  const db = await open();
  try {
    return await new Promise<T | null>((resolve, reject) => {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
      request.onsuccess = () => resolve((request.result as T) ?? null);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

export async function saveDeviceKey(pair: CryptoKeyPair): Promise<void> {
  await put("devicePrivateKey", pair.privateKey);
  await put("devicePublicKey", pair.publicKey);
}

export async function loadDeviceKey(): Promise<CryptoKeyPair | null> {
  const privateKey = await get<CryptoKey>("devicePrivateKey");
  const publicKey = await get<CryptoKey>("devicePublicKey");
  return privateKey && publicKey ? { privateKey, publicKey } : null;
}

/** Also clears a dead state: a new credential, from enrolment or renewal, is
 *  a live one, and a card that kept the old verdict would send its holder to
 *  enrol again right after they did. */
export async function saveCredential(bytes: Uint8Array): Promise<void> {
  await put("credential", bytes);
  await put("credentialState", null);
}

/** The issuer's refusal to renew, by its `detail`: `revoked`,
 *  `left-directory` or `expired-too-long`. Any of them means the holder has to
 *  enrol again with a password, and the card shows the form. */
export interface CredentialState {
  state: "dead";
  detail: string;
}

export async function saveCredentialState(state: "dead", detail: string): Promise<void> {
  await put("credentialState", { state, detail });
}

export async function loadCredentialState(): Promise<CredentialState | null> {
  return get<CredentialState>("credentialState");
}

/** A status list whose signature `sync.ts` already checked. */
export async function saveStatusList(list: StoredStatusList): Promise<void> {
  await put("statusList", list);
}

export async function loadStatusList(): Promise<StoredStatusList | null> {
  const stored = await get<StoredStatusList>("statusList");
  return stored ? { bitmap: new Uint8Array(stored.bitmap), issuedAt: stored.issuedAt } : null;
}

/** Device seconds, not corrected ones, at the last sync that reached the
 *  issuer. The clock alarms compare it with the device clock, which is the
 *  one that might have been moved since. */
export async function saveLastSyncAt(deviceSeconds: number): Promise<void> {
  await put("lastSyncAt", deviceSeconds);
}

export async function loadLastSyncAt(): Promise<number | null> {
  return get<number>("lastSyncAt");
}

export async function loadCredential(): Promise<Uint8Array | null> {
  const stored = await get<Uint8Array>("credential");
  return stored ? new Uint8Array(stored) : null;
}

/** The issuer keys this wallet verifies against. Written at enrolment, read
 *  at every scan; `verify.ts` refuses with `unknown-issuer-key` when the map
 *  is empty, which is what a device that never enrolled holds. */
export async function saveTrustStore(keys: Map<number, Uint8Array>): Promise<void> {
  await put("trustStore", keys);
}

export async function loadTrustStore(): Promise<Map<number, Uint8Array>> {
  const stored = await get<Map<number, Uint8Array>>("trustStore");
  return new Map(
    [...(stored ?? new Map<number, Uint8Array>())].map(([kid, raw]) => [kid, new Uint8Array(raw)]),
  );
}

export async function saveClockOffset(offsetSeconds: number): Promise<void> {
  await put("clockOffset", { offsetSeconds, recordedAt: Date.now() / 1000 });
}

export async function loadClockOffset(): Promise<number | null> {
  const stored = await get<{ offsetSeconds: number }>("clockOffset");
  return stored ? stored.offsetSeconds : null;
}

export async function correctedNow(): Promise<{ now: number; synced: boolean }> {
  const offset = await loadClockOffset();
  const device = Math.floor(Date.now() / 1000);
  return offset === null ? { now: device, synced: false } : { now: device + offset, synced: true };
}

export async function rememberNonce(nonce: Uint8Array): Promise<void> {
  await update<Record<string, number>>("nonces", (seen) => {
    const now = Date.now() / 1000;
    const kept = Object.fromEntries(
      Object.entries(seen ?? {}).filter(([, at]) => now - at < NONCE_MEMORY_S),
    );
    kept[Array.from(nonce).join(",")] = now;
    return kept;
  });
}

export async function recentNonces(): Promise<Set<string>> {
  const seen = (await get<Record<string, number>>("nonces")) ?? {};
  const now = Date.now() / 1000;
  return new Set(
    Object.entries(seen).filter(([, at]) => now - at < NONCE_MEMORY_S).map(([key]) => key),
  );
}

/**
 * The portrait-less credential the issuer signs beside every credential, used
 * only inside errands. `null` when the issuer sent none: a wallet from before
 * VS3 until its next renewal, or an issuer that predates it. Enrolment and
 * renewal always write it, so a lite credential from an earlier enrolment,
 * bound to a device key this phone no longer has, never outlives the
 * credential it came with.
 */
export async function saveCredentialLite(bytes: Uint8Array | null): Promise<void> {
  await put("credentialLite", bytes);
}

export async function loadCredentialLite(): Promise<Uint8Array | null> {
  const stored = await get<Uint8Array>("credentialLite");
  return stored ? new Uint8Array(stored) : null;
}

/** An errand this holder received, as scanned, with its expiry beside it so
 *  pruning does not have to decode. */
interface StoredErrand {
  bytes: Uint8Array;
  expiresAt: number;
}

const sameBytes = (a: Uint8Array, b: Uint8Array) =>
  a.length === b.length && a.every((byte, i) => byte === b[i]);

/** Keeps an errand `errand-receive` already verified. Scanning the same one
 *  twice stores it once. */
export async function saveErrand(bytes: Uint8Array, expiresAt: number): Promise<void> {
  await update<StoredErrand[]>("errands", (stored) => {
    const kept = (stored ?? []).filter((errand) => !sameBytes(new Uint8Array(errand.bytes), bytes));
    return [...kept, { bytes, expiresAt }];
  });
}

/** The errands still alive at `now`, oldest first. Expired ones are dropped
 *  from storage on the way: an expired errand is refused by every verifier, so
 *  listing it on the card would only offer the holder a code that fails. */
export async function liveErrands(now: number): Promise<Uint8Array[]> {
  let live: StoredErrand[] = [];
  await update<StoredErrand[]>("errands", (stored) => {
    live = (stored ?? []).filter((errand) => errand.expiresAt > now);
    return live;
  });
  return live.map((errand) => new Uint8Array(errand.bytes));
}

/**
 * Receipts this phone signed and has not uploaded, base64url, oldest first.
 * Strings rather than bytes so the upload sends them as they are and removes
 * the ones it sent by value: a receipt recorded while a batch is in flight is
 * not in that batch and must survive its removal.
 */
export async function loadReceiptQueue(): Promise<string[]> {
  return (await get<string[]>("receiptQueue")) ?? [];
}

/** One read-modify-write of the queue, for the same reason as the nonces: a
 *  scan and an upload finishing together must not drop each other's change. */
export async function updateReceiptQueue(change: (queue: string[]) => string[]): Promise<void> {
  await update<string[]>("receiptQueue", (queue) => change(queue ?? []));
}

/**
 * The strong-mode challenges this verifier issued and is still waiting on:
 * nonce key to the device second it was issued. One read-modify-write, like
 * the nonces, because consuming a challenge has to be a single step: two scans
 * of the same answer finishing together must not both find it pending.
 * `challenges.ts` holds the rules; this is only where they are kept.
 */
export async function updateChallenges(
  change: (pending: Record<string, number>) => Record<string, number>,
): Promise<void> {
  await update<Record<string, number>>("challenges", (pending) => change(pending ?? {}));
}

/**
 * Logging out: everything this application ever stored on the device, gone in
 * one transaction.
 *
 * The store is cleared whole rather than key by key, so a key added to this
 * file later is wiped without anyone remembering to list it here. IndexedDB is
 * the only storage the application writes. The service worker's cache is
 * another thing and stays: it holds the application's own static files and
 * nothing of the person, and without it the enrolment form would not open
 * offline.
 *
 * Cleared, not deleted with `deleteDatabase`: a deletion waits behind every
 * connection still open, in this tab or another, and resolves only when they
 * close, so a logout would hang on a second tab left open.
 */
export async function wipeDevice(): Promise<void> {
  const db = await open();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}
